import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

import { artifactMachineIdOf } from "./artifactMachine";
import type { ConversationFeedItem } from "./client";
import type { Announcement, Ref } from "./contract";
import type { ChannelApi } from "./useChannel";

// ── the ONE stream/prioritized derivation ─────────────────────────────────────────
// A conversation is ONE object: its announcements. The stream renders them;
// prioritized is the SAME rows flagged by the on-write backref (artifacts.announce /
// the prioritize verb merge body.prioritized). Never a second list, never a second query.
// A conversation view shows ONLY its own conversation's announcements. Identity
// is STRICT: with NO current conversation the view shows NOTHING (a lawful
// empty middle),
// never the cross-conversation soup — the ws delivers the WHOLE user's stream
// (every conversation), so an unscoped match leaked other conversations'
// announcements AND their pins onto the open view. Conversation identity never
// doubles as loading state: the oracle-declared machine status owns waiting.
export const inConversation = (a: Announcement, conversationId: string): boolean =>
  !!conversationId && a.conversationId === conversationId;

// THE PRIORITIZED LIST IS FEED-LEVEL (Ori 2026-07-24 — the pinned rail died
// into the feed's right rail): the derivation spans EVERY conversation's
// announcements — the rail axis is decoupled from the open conversation.
export const prioritizedAnnouncementsOf = (announcements: Announcement[]): Announcement[] =>
  announcements.filter((a) => a.prioritized?.active === true);

export type ConversationMachine = {
  id?: string;
  name?: string;
  label?: string;
};

// prioritized VIEW state only — the prioritized rows themselves derive from the
// announcement store (prioritizedAnnouncementsOf), never live here.
type PrioritizedState = {
  selectedId: string;
};

// the rail open states are WORLD STATE now (feed:left:@binary /
// feed:right:@binary on the feed row) — no client panel state survives.

type ConversationState = {
  key: string;
  conversationId: string;
  machine: ConversationMachine | null;
  displayLabel: string;
  scope: Ref[];
  prioritized: PrioritizedState;
  streamArtifactVisibility: Record<string, Record<string, true>>;
  participantFilter: string;
  unresolved: string;
};

type ConversationWorkspaceApi = {
  workspaceError: string;
  key: string;
  conversationId: string;
  machine: ConversationMachine | null;
  displayLabel: string;
  scope: Ref[];
  scopePath: string;
  prioritized: PrioritizedState;
  streamArtifactVisible: Record<string, boolean>;
  participantFilter: string;
  unresolved: string;
  setScope: (scope: Ref[]) => void;
  setActiveConversation: (conversationId: string, machine?: unknown) => void;
  setActiveConversationId: (conversationId: string, label?: string) => void;
  clearActiveConversation: () => void;
  setWorkspaceError: (message: string) => void;
  openConversation: (item: ConversationFeedItem) => void;
  setSelectedPrioritizedId: (id: string) => void;
  setStreamArtifactVisible: (artifactId: string, sourceId: string, visible: boolean) => void;
  setParticipantFilter: (id: string) => void;
};

const DEFAULT_PRIORITIZED: PrioritizedState = {
  selectedId: "",
};

const stateOf = (key: string, conversationId = "", machine: ConversationMachine | null = null): ConversationState => ({
  key,
  conversationId,
  machine,
  displayLabel: "",
  scope: [],
  prioritized: DEFAULT_PRIORITIZED,
  streamArtifactVisibility: {},
  participantFilter: "",
  unresolved: "",
});

const machineOf = (machine: unknown): ConversationMachine | null => (
  machine && typeof machine === "object" && !Array.isArray(machine) ? machine as ConversationMachine : null
);

// The conversation IS its id. Identity is canonical = conversationId (the box
// machine id for a surface, the minted/claimed id for a workspace). A real
// conversationId ALWAYS wins; the machine is display metadata and is only the
// fallback for a machine-only seed with no id yet. Keying by the machine FIRST
// was the bug: a feed row carrying the COMMAND conversation's machine keyed onto
// the command conversation, so activating a surface never changed the active key
// and the stream kept rendering the previous conversation.
const canonicalConversationId = (conversationId: string, machine?: unknown): string => {
  const m = machineOf(machine);
  return String(conversationId || m?.id || m?.name || "");
};

const Context = createContext<ConversationWorkspaceApi | null>(null);

export const ConversationWorkspaceProvider: React.FC<{
  ch: ChannelApi;
  children: React.ReactNode;
  onOpenMain: () => void;
  onJump: (id: string) => void;
  onActiveConversationId?: (id: string) => void;
}> = ({ ch, children, onOpenMain, onJump, onActiveConversationId }) => {
  const [activeKey, setActiveKey] = useState("");
  const [workspaceError, setWorkspaceError] = useState("");
  const [states, setStates] = useState<Record<string, ConversationState>>({
    "": stateOf(""),
  });

  const current = states[activeKey] || states[""] || stateOf("");

  const clearActiveConversation = useCallback(() => {
    setActiveKey("");
    setWorkspaceError("");
    onActiveConversationId?.("");
  }, [onActiveConversationId]);

  const update = useCallback((key: string, fn: (state: ConversationState) => ConversationState) => {
    setStates((prev) => {
      const existing = prev[key] || stateOf(key);
      return { ...prev, [key]: fn(existing) };
    });
  }, []);

  const setStreamArtifactVisible = useCallback((artifactId: string, sourceId: string, visible: boolean) => {
    if (!artifactId || !sourceId) return;
    update(activeKey, (state) => {
      const currentSources = state.streamArtifactVisibility[artifactId] || {};
      if (visible) {
        if (currentSources[sourceId]) return state;
        return {
          ...state,
          streamArtifactVisibility: {
            ...state.streamArtifactVisibility,
            [artifactId]: { ...currentSources, [sourceId]: true },
          },
        };
      }
      if (!currentSources[sourceId]) return state;
      const nextSources = { ...currentSources };
      delete nextSources[sourceId];
      const nextVisibility = { ...state.streamArtifactVisibility };
      if (Object.keys(nextSources).length) {
        nextVisibility[artifactId] = nextSources;
      } else {
        delete nextVisibility[artifactId];
      }
      return {
        ...state,
        streamArtifactVisibility: nextVisibility,
      };
    });
  }, [activeKey, update]);

  const activate = useCallback((conversationId: string, machine?: unknown, unresolved = "", displayLabel = "") => {
    const m = machineOf(machine);
    const key = canonicalConversationId(conversationId, machine);
    if (!key) {
      update(activeKey, (state) => ({ ...state, unresolved }));
      return "";
    }
    // key === conversationId: the state slot and the stream/prioritized filter share
    // the ONE identity, so activating a conversation always switches the view.
    setActiveKey(key);
    setWorkspaceError("");
    onActiveConversationId?.(key);
    update(key, (state) => ({
      ...state,
      key,
      conversationId: key,
      machine: m || state.machine,
      displayLabel: displayLabel || state.displayLabel,
      unresolved: "",
    }));
    return key;
  }, [activeKey, onActiveConversationId, update]);

  const api = useMemo<ConversationWorkspaceApi>(() => {
    const setScope = (scope: Ref[]) => update(current.key, (state) => ({ ...state, scope }));
    const setActiveConversation = (conversationId: string, machine?: unknown) => {
      activate(conversationId, machine, "Conversation machine id is missing.");
    };
    const setActiveConversationId = (conversationId: string, label?: string) => {
      activate(
        conversationId,
        undefined,
        "Conversation id is missing.",
        label,
      );
    };
    const openConversation = (item: ConversationFeedItem) => {
      const artifact = item.artifact && typeof item.artifact === "object" && !Array.isArray(item.artifact)
        ? item.artifact as { machine?: { id?: string; name?: string; label?: string }; type?: string; suffix?: string; position?: string }
        : undefined;
      const surfaceMachine = artifact?.machine;
      const machine = item.conversationMachine || surfaceMachine;
      const machineIdentity = machineOf(machine);
      const itemArtifactId = artifactMachineIdOf(item.artifact);
      // Connector surface chat is per surface. The feed row can still carry the
      // command/intent conversation, but opening the surface must target the
      // surface conversation so prompt replies get the folded artifact context.
      const conversationId = itemArtifactId.startsWith("connector_surface_")
        ? itemArtifactId
        : item.conversationId || String(machineIdentity?.id || machineIdentity?.name || "");
      const conversationKey = canonicalConversationId(conversationId, machine);
      const key = conversationKey
        ? activate(conversationId, machine, "Conversation machine id is missing.")
        : "";
      const targetKey = key || (item.artifact ? current.key : "");
      if (!targetKey) return;
      update(targetKey, (state) => ({
        ...state,
        // opening a conversation is conversation ACTIVATION, never breadcrumb
        // navigation — scope stays untouched. Prioritized rows DERIVE from the
        // announcement store; current tab selection lives in the world-owned
        // feed projection, never in this identity state.
        prioritized: itemArtifactId ? { selectedId: itemArtifactId } : state.prioritized,
      }));
      onOpenMain();
      setWorkspaceError("");
      // Seed the opened world feed carrier before history hydration so later
      // artifact-less replies can fold into their target box. A failed context
      // fetch surfaces as the workspace ERROR state — never an empty stream
      // ("No announcements yet." hiding an upstream 500).
      if (conversationId) {
        // an explicit rail open REFRESHES the context: resolve the same
        // conversation and re-pull its latest history (a missed ws frame must
        // not read as a conversation without its reply).
        void ch.ensureConversation(conversationId, item, { refresh: true }).catch((error) => {
          setWorkspaceError(error instanceof Error ? error.message : String(error));
        });
      }
      if (artifact && item.announcementId && !item.prioritized?.active) {
        // the prioritize WRITE goes through the world verb; the world merges the
        // backref onto the announcement and prioritizeArtifact mirrors it on the
        // store — the rail re-derives, no bookkeeping here. Preview-only
        // conversation/template cards have no announcement carrier and are
        // deliberately not prioritizable at open.
        void ch.prioritizeArtifact({
          conversationId: conversationId || conversationKey,
          artifact,
          artifactId: String(artifact.machine?.id || artifact.machine?.name || item.id),
          active: true,
          label: artifact.machine?.label || item.title || conversationKey,
        }).catch((error) => {
          setWorkspaceError(error instanceof Error ? error.message : String(error));
        });
      }
      window.setTimeout(() => onJump(item.announcementId || item.id), 0);
    };
    const setSelectedPrioritizedId = (selectedId: string) => update(current.key, (state) => ({
      ...state,
      prioritized: { selectedId },
    }));
    const setParticipantFilter = (participantFilter: string) => update(current.key, (state) => ({
      ...state,
      participantFilter,
    }));
    return {
      workspaceError,
      key: current.key,
      conversationId: current.conversationId,
      machine: current.machine,
      displayLabel: current.displayLabel,
      scope: current.scope,
      scopePath: current.scope[current.scope.length - 1]?.path || "",
      prioritized: current.prioritized,
      streamArtifactVisible: Object.fromEntries(
        Object.entries(current.streamArtifactVisibility).map(([artifactId, sources]) => [
          artifactId,
          Object.keys(sources).length > 0,
        ]),
      ),
      participantFilter: current.participantFilter,
      unresolved: current.unresolved,
      setScope,
      setActiveConversation,
      setActiveConversationId,
      clearActiveConversation,
      setWorkspaceError,
      openConversation,
      setSelectedPrioritizedId,
      setStreamArtifactVisible,
      setParticipantFilter,
    };
  }, [activate, ch, clearActiveConversation, current, onJump, onOpenMain, setStreamArtifactVisible, update, workspaceError]);

  return <Context.Provider value={api}>{children}</Context.Provider>;
};

export const useConversationWorkspace = (): ConversationWorkspaceApi => {
  const value = useContext(Context);
  if (!value) throw new Error("ConversationWorkspaceProvider required");
  return value;
};
